import React from 'react';
import { Link } from 'react-router-dom';
import ModulePill from './ModulePill';

const ModuleCard = ({ module, viewerRolePath, studyDeck = null }) => {
  const isPublished = module.status === 'published';
  const isLockedForStudent = viewerRolePath === '/student' && !isPublished;
  const topics = module.topics || [];
  const topicCount = topics.length;
  const firstTopic = topics[0];

  // Prefer the module's explicit description (edited by developer).
  // Fall back to a clean snippet from the first topic summary.
  const fallback = stripHtmlClean(firstTopic?.summary || '').slice(0, 200);
  const blurb = (module.description && module.description.trim()) || fallback;

  return (
    <div className="card p-5 flex flex-col" data-testid={`module-card-${module.order}`}>
      <div className="flex items-start justify-between mb-3">
        <ModulePill number={module.order} />
        <span className={isPublished ? 'badge-published' : 'badge-draft'}>
          {isPublished ? 'Published' : 'Coming Soon'}
        </span>
      </div>

      <h3 className="text-lg font-bold text-gray-900 leading-snug mb-2" data-testid={`module-title-${module.order}`}>
        {module.title}
      </h3>
      <div className="text-xs text-gray-500 mb-2">
        {topicCount === 0 ? 'No topics yet' : `${topicCount} topic${topicCount === 1 ? '' : 's'}`}
        {studyDeck && (
          <> · <span className="text-amber-700 font-semibold">
            {studyDeck.card_count} recall cards
            {studyDeck.due_count > 0 && <> ({studyDeck.due_count} due)</>}
          </span></>
        )}
      </div>
      <div className="text-sm text-gray-600 line-clamp-3 mb-4 flex-1">
        {blurb || <span className="italic text-gray-400">No description yet.</span>}
      </div>

      <div className="flex gap-2">
        {isLockedForStudent ? (
          <button disabled className="btn-primary opacity-60 cursor-not-allowed flex-1 text-sm"
                  data-testid={`module-locked-${module.order}`}>
            Locked
          </button>
        ) : (
          <Link to={`${viewerRolePath}/module/${module.id}${firstTopic ? `?topic=${firstTopic.id}` : ''}`}
                className="btn-primary flex-1 text-sm text-center"
                data-testid={`module-open-${module.order}`}>
            Open
          </Link>
        )}
        {studyDeck && !isLockedForStudent && (
          <Link
            to={`/study/session?deck=${studyDeck.id}`}
            data-testid={`module-practice-${module.order}`}
            title={`${studyDeck.card_count} Recall/Connect flashcards for this module`}
            className="text-sm text-center px-3 py-2 rounded-md font-semibold"
            style={{
              background: '#FCD359',
              color: '#0E2A47',
              border: '1px solid #E0B73D',
              boxShadow: '0 1px 3px rgba(224,183,61,0.35)',
            }}
          >
            🧠 Practice
          </Link>
        )}
      </div>
    </div>
  );
};

/**
 * Strip HTML tags AND decode common HTML entities so card previews don't
 * show artifacts like `&nbsp;`, `&amp;`, `<p>`, `&#39;` etc.
 */
const stripHtmlClean = (s) => {
  if (!s) return '';
  // Use the DOM to decode entities and pull innerText (handles &nbsp;, &amp;, etc.)
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = s;
    return (tmp.innerText || tmp.textContent || '').replace(/\s+/g, ' ').trim();
  }
  // Fallback for SSR (shouldn't hit in our SPA)
  return s.replace(/<[^>]*>/g, ' ').replace(/&\w+;/g, ' ').replace(/\s+/g, ' ').trim();
};

export default ModuleCard;
